import React, { useEffect, useMemo, useState } from 'react';
import { palette, status } from '../../../../style-utils/theme';
import { Text } from '../../../../style-utils/text';
import {
  InlineMessage,
  Presence,
  Shake,
  Staleable,
  StateSwitch,
} from '../../../../motion';
import {
  ListSkeleton,
  skeletonHeight,
} from '../dashboard-shared-components/load-state';
/* WP-37 — the shared table engine. This screen drew its rows as
   percentage-width flex divs with no table element, no roles, no scope and no
   header association at all; the module header records the mobile contract it
   now inherits. */
import {
  BodyRow,
  CellFlex,
  Table,
  TableCaption,
  TableFrame,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../../../table/list-table';
import {
  Avatar,
  AvatarFallback,
  Column,
  CountPill,
  DashboardContent,
  EmptyState,
  EmptyStateIcon,
  InlineError,
  ManageButton,
  RolePill,
  ScopesListing,
  SearchInputContainer,
  StyledInput,
  Title,
  TruncatedCell,
  WarningBanner,
  WarningIcon,
} from './dashboard-scopes.styled';
import { DashboardStaffScopeDetail } from './dashboard-staff-scope-detail';
import {
  displayName,
  getInitials,
  ORG_WIDE_EXPLANATIONS,
  ORG_WIDE_SHORT,
  orgWideTone,
} from './scope-vocabulary';
import { ScopeSummary } from '../../../../interfaces/hierarchy-interfaces';
import {
  HierarchyApiError,
  getScopeSummary,
} from '../../../../api/hierarchy-client';
import { PaginationButtons } from '../dashboard-shared-components/table-pagination/table-pagination';
import { usePagination } from '../../../hooks/use-pagination';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import {
  ROLES,
  normalizeRole,
  outranks,
  roleLabel,
} from '../../../../auth/roles';

/* The list's rows are the table's own height; the skeleton reserves it. */
const SKELETON_ROWS = 5;
// Measured on the rendered table at 1366px: the avatar row.
const ROW_HEIGHT = 92;

export const DashboardScopes: React.FC = () => {
  const { permissions, user } = useAuth();
  const canReadScope = can(permissions, PERMISSIONS.SCOPE_READ);
  const holdsScopeManage = can(permissions, PERMISSIONS.SCOPE_MANAGE);

  // MIRRORED, NOT ENFORCED HERE — staff-scopes.js refuses these regardless
  // (403 "You cannot modify your own access scope. Ask another owner."). This
  // row offered "Manage access" unconditionally, including on the signed-in
  // owner's own row, and the guard only engaged one screen later. Same
  // predicate the Users list has applied per row since it was written: never
  // yourself, never an Owner, and only someone strictly below you.
  const actorRole = normalizeRole(user?.role);
  const mayAdminister = (row: ScopeSummary): boolean =>
    holdsScopeManage &&
    String(row.user_id) !== String(user?.id ?? '') &&
    normalizeRole(row.role) !== ROLES.OWNER &&
    outranks(actorRole, normalizeRole(row.role));

  const [rows, setRows] = useState<ScopeSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Held in state rather than routed, so this screen works on its own without
  // touching the shared router.
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!canReadScope) {
      setLoading(false);

      return undefined;
    }

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getScopeSummary();

        if (!cancelled) {
          setRows(response);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof HierarchyApiError
              ? loadError.message
              : 'Could not load staff access.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [canReadScope, selectedUserId]);

  const isSearching = searchTerm.trim().length > 0;

  const filteredRows = useMemo(() => {
    if (!isSearching) {
      return rows;
    }

    const term = searchTerm.trim().toLowerCase();

    return rows.filter(row => {
      const name = (row.name || '').toLowerCase();
      const email = (row.email || '').toLowerCase();
      const role = roleLabel(row.role).toLowerCase();

      return name.includes(term) || email.includes(term) || role.includes(term);
    });
  }, [rows, searchTerm, isSearching]);

  const {
    currentPage,
    displayedData,
    nextPage,
    previousPage,
    hasPreviousPage,
    hasNextPage,
    getTotalPages,
  } = usePagination(filteredRows);

  const pageRows = displayedData as ScopeSummary[];
  const totalPages = getTotalPages();

  // Staff who can sign in and see nothing. Invisible on every other screen,
  // which is why it gets a banner rather than only a pill on the row.
  //
  // `org_wide` is the half this count was missing. building_count is a COUNT
  // over access_scope, and it is legitimately 0 for somebody who reaches every
  // building — an owner (whose reach is a role short-circuit, so no grant row
  // exists) and, when scope enforcement is off, EVERY staff member in the
  // organisation. Counting those as stranded put a warning on the owner's own
  // row and, in a non-enforcing org, claimed the whole company reached nothing.
  const strandedCount = useMemo(
    () => rows.filter(row => row.building_count === 0 && !row.org_wide).length,
    [rows]
  );

  if (selectedUserId) {
    return (
      <DashboardStaffScopeDetail
        userId={selectedUserId}
        onBack={() => setSelectedUserId(null)}
      />
    );
  }

  if (!canReadScope) {
    return (
      <DashboardContent>
        <Column>
          <EmptyState>
            <EmptyStateIcon>&#128274;</EmptyStateIcon>
            <Text fontSize={18} fontWeight={700} mb={0} color={palette.ink}>
              Staff access is restricted
            </Text>
            <Text fontSize={14} mt={0} mb={0} color={palette.slate}>
              You do not have permission to see who governs what.
            </Text>
          </EmptyState>
        </Column>
      </DashboardContent>
    );
  }

  return (
    <DashboardContent id="dashboard-scopes-content">
      <ScopesListing>
        <Title>
          <Text
            mt={4}
            mb={0}
            fontSize={18}
            fontWeight={700}
            color={palette.ink}
          >
            Staff access
          </Text>
          <Text mt={4} mb={0} fontSize={12} color={palette.slate}>
            Which part of the business each person governs
          </Text>
        </Title>

        <SearchInputContainer>
          <StyledInput
            name="staffScopeSearch"
            type="text"
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            placeholder="Search staff…"
          />
        </SearchInputContainer>
      </ScopesListing>

      <Column>
        {/* A refetch that FAILED over rows already on screen: say so above
            the table rather than replacing it. A first-load failure is the
            'error' fill of the switch below. */}
        <InlineMessage
          role="alert"
          message={
            error && rows.length > 0 ? (
              <Shake active>
                <InlineError>
                  <span aria-hidden="true">&#9888;</span>
                  <span>{error}</span>
                </InlineError>
              </Shake>
            ) : undefined
          }
        />

        {strandedCount > 0 && !loading && (
          <WarningBanner>
            <WarningIcon aria-hidden="true">&#9888;</WarningIcon>
            <div>
              <Text
                mt={0}
                mb={0}
                fontSize={14}
                fontWeight={600}
                color={status.warning.fg}
              >
                {strandedCount}{' '}
                {strandedCount === 1
                  ? 'staff member reaches no building'
                  : 'staff members reach no building'}
              </Text>
              <Text mt={4} mb={0} fontSize={14} color={palette.slate}>
                They can sign in, but every list will be empty until they are
                granted a scope.
              </Text>
            </div>
          </WarningBanner>
        )}

        {/* ONE BOX, FOUR FILLS. The spinner-and-sentence that stood here is a
            skeleton shaped like the rows it precedes; StateSwitch crossfades
            it, the error, the empty state and the table inside one container
            with a reserved floor. Coming BACK from a detail screen re-runs the
            load over rows already held, which is a refresh, not a first load:
            the table stays and dims (Staleable). */}
        <StateSwitch
          state={
            loading && rows.length === 0
              ? 'loading'
              : error && rows.length === 0
                ? 'error'
                : pageRows.length === 0
                  ? 'empty'
                  : 'ready'
          }
          minHeight={skeletonHeight(SKELETON_ROWS, ROW_HEIGHT)}
        >
          {loading && rows.length === 0 ? (
            <ListSkeleton
              rows={SKELETON_ROWS}
              rowHeight={ROW_HEIGHT}
              label="staff access"
            />
          ) : error && rows.length === 0 ? (
            <Shake active>
              <InlineError role="alert">
                <span aria-hidden="true">&#9888;</span>
                <span>{error}</span>
              </InlineError>
            </Shake>
          ) : pageRows.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>&#128273;</EmptyStateIcon>
              <Text fontSize={18} fontWeight={700} mb={0} color={palette.ink}>
                {isSearching ? 'No matching staff' : 'No staff members yet'}
              </Text>
              <Text fontSize={14} mt={0} mb={0} color={palette.slate}>
                {isSearching
                  ? 'Try a different name, email, or role.'
                  : 'Staff you invite will appear here once they join.'}
              </Text>
            </EmptyState>
          ) : (
            <Staleable stale={loading}>
              <TableFrame>
                <Table>
                  <TableCaption>
                    Staff members, the access each one holds, and how many
                    buildings it reaches
                  </TableCaption>
                  <Thead>
                    <Tr>
                      <Th width={26}>Staff member</Th>
                      <Th width={20}>Email</Th>
                      <Th width={16}>Role</Th>
                      <Th width={12}>Grants</Th>
                      <Th width={12}>Buildings</Th>
                      <Th width={14} style={{ textAlign: 'right' }}>
                        Action
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {/* Rows arrive in a cascade and leave inside a Presence;
                        keyed by page and search so a page turn or a narrowing
                        swaps the set instantly and replays the cascade. */}
                    <Presence key={`${currentPage}:${searchTerm}`}>
                      {pageRows.map((row, index) => {
                        const label = displayName(row.name, row.email);
                        // Same correction as strandedCount: 0 buildings only
                        // means "reaches nothing" when they do not reach
                        // everything by role or by an org-wide grant.
                        const isStranded =
                          row.building_count === 0 && !row.org_wide;
                        const orgWideReason = row.org_wide
                          ? row.org_wide_reason
                          : null;
                        const canManageRow = mayAdminister(row);
                        // BELT AND BRACES FOR QA-02. The API now excludes
                        // deactivated staff from this list by default, so in
                        // normal operation this is never true. It is here
                        // because the consequence of being wrong is an owner
                        // reading a false answer to "who can reach what": if
                        // an inactive identity ever does arrive — through
                        // ?include=inactive, or from a build where the server
                        // filter is not deployed yet — it must not be
                        // indistinguishable from a colleague who can actually
                        // sign in.
                        const isInactive =
                          row.status != null && row.status !== 'active';

                        return (
                          <BodyRow key={row.user_id} index={index}>
                            <Td data-label="Staff member">
                              <CellFlex>
                                <Avatar>
                                  <AvatarFallback>
                                    {getInitials(row.name || row.email)}
                                  </AvatarFallback>
                                </Avatar>
                                <TruncatedCell
                                  style={{
                                    color: palette.ink,
                                    fontWeight: 500,
                                  }}
                                  title={label}
                                >
                                  {label}
                                </TruncatedCell>
                              </CellFlex>
                            </Td>

                            <Td data-label="Email">
                              <TruncatedCell title={row.email || undefined}>
                                {row.email || '—'}
                              </TruncatedCell>
                            </Td>

                            <Td data-label="Role">
                              <RolePill
                                isOwner={normalizeRole(row.role) === ROLES.OWNER}
                              >
                                {roleLabel(row.role)}
                              </RolePill>
                              {isInactive && (
                                <CountPill
                                  tone="warning"
                                  title="This person has been deleted. They cannot sign in, and their grants were revoked."
                                >
                                  Inactive
                                </CountPill>
                              )}
                            </Td>

                            <Td data-label="Grants">
                              <CountPill tone="neutral">
                                {row.grant_count}{' '}
                                {row.grant_count === 1 ? 'grant' : 'grants'}
                              </CountPill>
                            </Td>

                            {/* Zero buildings is the outcome worth flagging: it
                                means every grant they hold resolves to nothing.
                                Unless they reach every building without one,
                                in which case the count is 0 and saying "None"
                                is the opposite of true. */}
                            <Td data-label="Buildings">
                              <CountPill
                                tone={
                                  orgWideReason
                                    ? orgWideTone(orgWideReason)
                                    : isStranded
                                      ? 'warning'
                                      : 'success'
                                }
                                title={
                                  orgWideReason
                                    ? ORG_WIDE_EXPLANATIONS[orgWideReason]
                                    : isStranded
                                      ? 'This person can sign in but will see nothing.'
                                      : undefined
                                }
                              >
                                {orgWideReason
                                  ? ORG_WIDE_SHORT
                                  : isStranded
                                    ? 'None'
                                    : `${row.building_count} ${
                                        row.building_count === 1
                                          ? 'building'
                                          : 'buildings'
                                      }`}
                              </CountPill>
                            </Td>

                            <Td data-label="Action" style={{ textAlign: 'right' }}>
                              {isInactive ? (
                                // No live control on a dead identity. Managing
                                // the access of somebody who cannot sign in is
                                // the misleading action the QA pass called out
                                // by name.
                                <ManageButton
                                  type="button"
                                  disabled
                                  title="Deleted people have no access to manage."
                                >
                                  No access
                                </ManageButton>
                              ) : canManageRow ? (
                                <ManageButton
                                  type="button"
                                  onClick={() => setSelectedUserId(row.user_id)}
                                >
                                  Manage access
                                </ManageButton>
                              ) : (
                                // Not hidden: who governs what is the question
                                // this screen answers, and a row that silently
                                // loses its control reads as a rendering fault.
                                // The reason is on the control instead.
                                <ManageButton
                                  type="button"
                                  onClick={() => setSelectedUserId(row.user_id)}
                                  title={
                                    String(row.user_id) === String(user?.id ?? '')
                                      ? 'You cannot change your own access. Ask another owner.'
                                      : normalizeRole(row.role) === ROLES.OWNER
                                        ? 'Owners reach every building by role; their access is not managed here.'
                                        : 'You can view this person’s access but not change it.'
                                  }
                                >
                                  View access
                                </ManageButton>
                              )}
                            </Td>
                          </BodyRow>
                        );
                      })}
                    </Presence>
                  </Tbody>
                </Table>
              </TableFrame>
            </Staleable>
          )}
        </StateSwitch>

        {pageRows.length > 0 && (
          <PaginationButtons
            currentPage={currentPage}
            hasPreviousPage={hasPreviousPage}
            hasNextPage={hasNextPage}
            previousPage={previousPage}
            nextPage={nextPage}
            totalPages={totalPages}
          />
        )}
      </Column>
    </DashboardContent>
  );
};
